import React, { useLayoutEffect, useRef, useState } from "react";
import {
  Box,
  Avatar,
  CircularProgress,
  Tooltip,
  Skeleton,
  Alert,
} from "@mui/material";
import { usePlayerPhoto } from "../hooks/usePlayerPhoto";
import {
  headshotImgIsReady,
  playerIdInitial,
  sportFetchesHeadshots,
} from "../utils/playerHeadshots";

/**
 * PlayerHeadshot Component
 *
 * Displays a player's headshot with intelligent fallbacks:
 * 1. Real player photo (if available)
 * 2. Generic headshot (if no real photo)
 * 3. Default avatar (if all else fails)
 *
 * @param {Object} props - Component props
 * @param {string} props.playerId - Player ID to display headshot for
 * @param {number} props.size - Size of the headshot in pixels (default: 120)
 * @param {string} props.variant - Avatar variant: 'circular', 'rounded', 'square' (default: 'circular')
 * @param {boolean} props.showTooltip - Whether to show tooltip with photo info (default: true)
 * @param {boolean} props.showLoading - Whether to show loading state (default: true)
 * @param {boolean} props.showError - Whether to show error state (default: false)
 * @param {Object} props.sx - Additional Material-UI sx styles
 * @param {number} props.expiration - URL expiration time in seconds (default: 3600)
 * @param {string} props.src - Optional photo URL; skips per-player photo fetches when set
 * @param {string} props.loading - img loading hint: 'lazy' | 'eager'
 * @param {string} props.fetchPriority - img fetchpriority: 'high' | 'low' | 'auto'
 * @param {string} props.sport - Sport hub: 'mlb' | 'nba' | 'nfl' (default: 'mlb')
 * @param {boolean} props.enabled - Whether to fetch photos (default: true)
 */
const PlayerHeadshot = ({
  playerId,
  src,
  size = 120,
  variant = "circular",
  showTooltip = true,
  showLoading = true,
  showError = false,
  sx = {},
  expiration = 3600,
  enabled = true,
  sport = "mlb",
  loading,
  fetchPriority,
  ...props
}) => {
  const [imageError, setImageError] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);
  const imgRef = useRef(null);
  const providedSrc = src || null;

  const {
    isLoading,
    error,
    hasPhoto: fetchedHasPhoto,
    isGeneric: fetchedIsGeneric,
    photoUrl: fetchedPhotoUrl,
    metadata,
  } = usePlayerPhoto(playerId, {
    expiration,
    enabled: enabled && !providedSrc && sportFetchesHeadshots(sport, playerId),
    sport,
  });

  const photoUrl = providedSrc || fetchedPhotoUrl;
  const hasPhoto = Boolean(providedSrc) || fetchedHasPhoto;
  const isGeneric = providedSrc ? false : fetchedIsGeneric;

  useLayoutEffect(() => {
    setImageError(false);
    const img = imgRef.current;
    setImageLoaded(
      headshotImgIsReady({
        loaded: false,
        error: false,
        complete: img?.complete,
        naturalWidth: img?.naturalWidth,
      })
    );
  }, [playerId, photoUrl]);

  // Handle image load error
  const handleImageError = () => {
    setImageError(true);
    setImageLoaded(false);
  };

  // Handle image load success
  const handleImageLoad = () => {
    setImageLoaded(true);
    setImageError(false);
  };

  // Generate tooltip text
  const getTooltipText = () => {
    if (isLoading) return "Loading photo...";
    if (error) return `Error: ${error}`;
    if (!hasPhoto) return "No photo available";
    if (isGeneric) return "Generic headshot";
    if (metadata) {
      return `Photo: ${metadata.width}x${
        metadata.height
      }px, ${metadata.format?.toUpperCase()}`;
    }
    return "Player headshot";
  };

  // Render loading state
  if (isLoading && showLoading) {
    return (
      <Box
        sx={{
          width: size,
          height: size,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          ...sx,
        }}
        {...props}
      >
        <CircularProgress size={size * 0.3} />
      </Box>
    );
  }

  // Render error state
  if (error && showError) {
    return (
      <Box
        sx={{
          width: size,
          height: size,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          ...sx,
        }}
        {...props}
      >
        <Alert severity="error" sx={{ fontSize: "0.75rem" }}>
          Photo Error
        </Alert>
      </Box>
    );
  }

  const imageReady = imageLoaded && !imageError;
  const renderSkeleton = !imageReady && photoUrl;

  // Determine what to display
  const displayContent = () => {
    // If we have a photo URL and no image error, show the photo
    if (photoUrl && !imageError) {
      return (
        <Box sx={{ position: "relative", width: "100%", height: "100%" }}>
          {renderSkeleton && (
            <Skeleton
              variant={variant}
              width="100%"
              height="100%"
              sx={{ position: "absolute", top: 0, left: 0 }}
            />
          )}
          <img
            ref={imgRef}
            src={photoUrl}
            alt={`${playerId} headshot`}
            loading={loading}
            {...(fetchPriority ? { fetchpriority: fetchPriority } : {})}
            onLoad={handleImageLoad}
            onError={handleImageError}
            style={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
              opacity: imageReady ? 1 : 0,
              transition: "opacity 0.3s ease-in-out",
            }}
          />
        </Box>
      );
    }

    // Fallback to default avatar
    return (
      <Avatar
        sx={{
          width: "100%",
          height: "100%",
          fontSize: size * 0.4,
          bgcolor: isGeneric ? "grey.400" : "primary.main",
        }}
        variant={variant}
      >
        {playerIdInitial(playerId)}
      </Avatar>
    );
  };

  const avatarContent = (
    <Box
      sx={{
        width: size,
        height: size,
        borderRadius:
          variant === "circular" ? "50%" : variant === "rounded" ? 2 : 0,
        overflow: "hidden",
        border: "2px solid",
        borderColor: isGeneric ? "grey.300" : "primary.main",
        ...sx,
      }}
      {...props}
    >
      {displayContent()}
    </Box>
  );

  // Wrap with tooltip if enabled
  if (showTooltip) {
    return (
      <Tooltip title={getTooltipText()} arrow>
        {avatarContent}
      </Tooltip>
    );
  }

  return avatarContent;
};

export default PlayerHeadshot;
